import { SearchPage } from './features/search/SearchPage';
import { CommunicationsPage } from '@/pages/platform/CommunicationsPage';
import { LatestUpdatesPage } from '@/pages/LatestUpdatesPage';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { SupportTickets } from '@/features/settings/SupportTickets';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'motion/react';
import { ToastProvider, TooltipProvider, NotFound, Spinner, ErrorState } from '@/components/ui';
import { createQueryClient } from '@/lib/queryClient';
import { SessionProvider, homePathFor, isPlatformOperator, useSession } from '@/lib/session';
import { ThemeProvider, useAdoptServerTheme } from '@/lib/theme';
import { StaffShell } from '@/components/shell/StaffShell';
import { PortalShell } from '@/components/shell/PortalShell';
import { PlatformShell } from '@/components/shell/PlatformShell';
import { ImpersonationBanner } from '@/components/shell/ImpersonationBanner';
import { PasswordChangeGate } from '@/components/shell/PasswordChangeGate';
import { MaintenanceGate } from '@/components/shell/MaintenanceScreen';
import { PlatformAppearanceBridge } from '@/lib/platformAppearance';

/* Route-level code splitting: each screen loads on first visit. */
const MoreHome = lazy(() => import('@/pages/more/MorePages').then((m) => ({ default: m.MoreHome })));
const OtherProductsPage = lazy(() => import('@/pages/more/MorePages').then((m) => ({ default: m.OtherProductsPage })));
const SuggestFeaturePage = lazy(() => import('@/pages/more/MorePages').then((m) => ({ default: m.SuggestFeaturePage })));
const GamificationPage = lazy(() => import('@/pages/more/MorePages').then((m) => ({ default: m.GamificationPage })));
const LandingPage = lazy(() => import('@/pages/marketing/LandingPage').then((m) => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import('@/pages/auth/Login').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/pages/auth/Signup').then((m) => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })));
const InvitePage = lazy(() => import('@/pages/auth/Invite').then((m) => ({ default: m.InvitePage })));
const WorkspacePickerPage = lazy(() => import('@/pages/WorkspacePicker').then((m) => ({ default: m.WorkspacePickerPage })));
const NoWorkspacePage = lazy(() => import('@/pages/WorkspacePicker').then((m) => ({ default: m.NoWorkspacePage })));
const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const ClientWorkspacePage = lazy(() => import('@/pages/clients/ClientWorkspacePage').then((m) => ({ default: m.ClientWorkspacePage })));
const EngagementsPage = lazy(() => import('@/pages/engagements/EngagementsPage').then((m) => ({ default: m.EngagementsPage })));
const NewEngagementPage = lazy(() => import('@/pages/engagements/NewEngagementPage').then((m) => ({ default: m.NewEngagementPage })));
const EngagementPage = lazy(() => import('@/pages/engagements/EngagementPage').then((m) => ({ default: m.EngagementPage })));
const PortalOverviewPage = lazy(() => import('@/pages/portal/PortalOverviewPage').then((m) => ({ default: m.PortalOverviewPage })));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ReviewQueuePage = lazy(() => import('@/pages/review/ReviewQueuePage').then((m) => ({ default: m.ReviewQueuePage })));
const ReviewWorkspacePage = lazy(() => import('@/pages/review/ReviewWorkspacePage').then((m) => ({ default: m.ReviewWorkspacePage })));
const PortalApprovalPage = lazy(() => import('@/pages/portal/PortalApprovalPage').then((m) => ({ default: m.PortalApprovalPage })));
const AuditExportsPage = lazy(() => import('@/pages/audit/AuditExportsPage').then((m) => ({ default: m.AuditExportsPage })));
const TemplatesPage = lazy(() => import('@/pages/library/TemplatesPage').then((m) => ({ default: m.TemplatesPage })));
const TemplateBuilderPage = lazy(() => import('@/pages/library/TemplateBuilderPage').then((m) => ({ default: m.TemplateBuilderPage })));
const PackagesPage = lazy(() => import('@/pages/library/PackagesPage').then((m) => ({ default: m.PackagesPage })));
const PackageDetailPage = lazy(() => import('@/pages/library/PackageDetailPage').then((m) => ({ default: m.PackageDetailPage })));
const AutomationPage = lazy(() => import('@/pages/automation/AutomationPage').then((m) => ({ default: m.AutomationPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AccountPage = lazy(() => import('@/pages/account/AccountPage').then((m) => ({ default: m.AccountPage })));
const PortalProfilePage = lazy(() => import('@/pages/portal/PortalProfilePage').then((m) => ({ default: m.PortalProfilePage })));
const PortalPeoplePage = lazy(() => import('@/pages/portal/PortalPeoplePage').then((m) => ({ default: m.PortalPeoplePage })));
const StaffFormPage = lazy(() => import('@/pages/forms/StaffFormPage').then((m) => ({ default: m.StaffFormPage })));
const PortalFormPage = lazy(() => import('@/pages/portal/PortalFormPage').then((m) => ({ default: m.PortalFormPage })));
const PortalAssetsPage = lazy(() => import('@/pages/portal/PortalAssetsPage').then((m) => ({ default: m.PortalAssetsPage })));
const PortalTasksPage = lazy(() => import('@/pages/portal/PortalTasksPage').then((m) => ({ default: m.PortalTasksPage })));
const PlatformTenantsPage = lazy(() => import('@/pages/platform/PlatformTenantsPage').then((m) => ({ default: m.PlatformTenantsPage })));
const PlatformPlansPage = lazy(() => import('@/pages/platform/PlatformPlansPage').then((m) => ({ default: m.PlatformPlansPage })));
const PlatformSupportGrantsPage = lazy(() => import('@/pages/platform/PlatformSupportGrantsPage').then((m) => ({ default: m.PlatformSupportGrantsPage })));
const PlatformTenantDetailPage = lazy(() => import('@/pages/platform/PlatformTenantDetailPage').then((m) => ({ default: m.PlatformTenantDetailPage })));
const PlatformUsersPage = lazy(() => import('@/pages/platform/PlatformUsersPage').then((m) => ({ default: m.PlatformUsersPage })));
const PlatformBillingPage = lazy(() => import('@/pages/platform/PlatformBillingPage').then((m) => ({ default: m.PlatformBillingPage })));
const PlatformCountriesPage = lazy(() => import('@/pages/platform/PlatformCountriesPage').then((m) => ({ default: m.PlatformCountriesPage })));
const PlatformEmailPage = lazy(() => import('@/pages/platform/PlatformEmailPage').then((m) => ({ default: m.PlatformEmailPage })));
const PlatformEmailLogsPage = lazy(() => import('@/pages/platform/PlatformEmailLogsPage').then((m) => ({ default: m.PlatformEmailLogsPage })));
const PlatformInactivityPage = lazy(() => import('@/pages/platform/PlatformInactivityPage').then((m) => ({ default: m.PlatformInactivityPage })));
const PlatformUsageReportsPage = lazy(() => import('@/pages/platform/PlatformUsageReportsPage').then((m) => ({ default: m.PlatformUsageReportsPage })));
const PlatformImpersonationPage = lazy(() => import('@/pages/platform/PlatformImpersonationPage').then((m) => ({ default: m.PlatformImpersonationPage })));
const PlatformSecurityPage = lazy(() => import('@/pages/platform/PlatformSecurityPage').then((m) => ({ default: m.PlatformSecurityPage })));
const PlatformGoogleLoginPage = lazy(() => import('@/pages/platform/PlatformGoogleLoginPage').then((m) => ({ default: m.PlatformGoogleLoginPage })));
const PlatformIpBlockerPage = lazy(() => import('@/pages/platform/PlatformIpBlockerPage').then((m) => ({ default: m.PlatformIpBlockerPage })));
const PlatformMaintenancePage = lazy(() => import('@/pages/platform/PlatformMaintenancePage').then((m) => ({ default: m.PlatformMaintenancePage })));
const PlatformLoginAlertsPage = lazy(() => import('@/pages/platform/PlatformLoginAlertsPage').then((m) => ({ default: m.PlatformLoginAlertsPage })));
const TwoFactorChallengePage = lazy(() => import('@/pages/auth/TwoFactorChallenge').then((m) => ({ default: m.TwoFactorChallengePage })));
const TwoFactorSettingsPage = lazy(() => import('@/pages/account/TwoFactorSettings').then((m) => ({ default: m.TwoFactorSettingsPage })));
const PlatformDashboardPage = lazy(() => import('@/pages/platform/PlatformDashboardPage').then((m) => ({ default: m.PlatformDashboardPage })));
const PlatformHealthPage = lazy(() => import('@/pages/platform/PlatformHealthPage').then((m) => ({ default: m.PlatformHealthPage })));
const PlatformAuditPage = lazy(() => import('@/pages/platform/PlatformAuditPage').then((m) => ({ default: m.PlatformAuditPage })));
const PlatformAiUsagePage = lazy(() => import('@/pages/platform/PlatformAiUsagePage').then((m) => ({ default: m.PlatformAiUsagePage })));
const PlatformAiProvidersPage = lazy(() => import('@/pages/platform/PlatformAiProvidersPage').then((m) => ({ default: m.PlatformAiProvidersPage })));
const PlatformAiKeysPage = lazy(() => import('@/pages/platform/PlatformAiKeysPage').then((m) => ({ default: m.PlatformAiKeysPage })));
const PlatformAiFirewallPage = lazy(() => import('@/pages/platform/PlatformAiFirewallPage').then((m) => ({ default: m.PlatformAiFirewallPage })));
const PlatformSettingsPage = lazy(() => import('@/pages/platform/PlatformSettingsPage').then((m) => ({ default: m.PlatformSettingsPage })));
const PlatformAppearancePage = lazy(() => import('@/pages/platform/PlatformAppearancePage').then((m) => ({ default: m.PlatformAppearancePage })));
const PlatformConnectorsPage = lazy(() => import('@/pages/platform/PlatformConnectorsPage').then((m) => ({ default: m.PlatformConnectorsPage })));
const PlatformApiPage = lazy(() => import('@/pages/platform/PlatformApiPage').then((m) => ({ default: m.PlatformApiPage })));
const PlatformWhiteLabelPage = lazy(() => import('@/pages/platform/PlatformWhiteLabelPage').then((m) => ({ default: m.PlatformWhiteLabelPage })));
const PlatformCouponsPage = lazy(() => import('@/pages/platform/PlatformCouponsPage').then((m) => ({ default: m.PlatformCouponsPage })));
const PlatformReferralsPage = lazy(() => import('@/pages/platform/PlatformReferralsPage').then((m) => ({ default: m.PlatformReferralsPage })));
const CheckoutPage = lazy(() => import('@/pages/checkout/CheckoutPage').then((m) => ({ default: m.CheckoutPage })));
const SignupPaymentPage = lazy(() => import('@/pages/checkout/SignupPaymentPage').then((m) => ({ default: m.SignupPaymentPage })));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));

const queryClient = createQueryClient();

function Boot() {
  return (
    <div className="boot" role="status" aria-live="polite">
      <div className="boot-inner">
        <Spinner size={20} />
        <span>Loading your workspace…</span>
      </div>
    </div>
  );
}

function ThemeBridge({ children }: { children: ReactNode }) {
  const { user } = useSession();
  return (
    <ThemeProvider signedIn={!!user}>
      <AdoptTheme pref={user?.themePreference} />
      <PlatformAppearanceBridge />
      {children}
    </ThemeProvider>
  );
}

function AdoptTheme({ pref }: { pref: 'light' | 'dark' | 'system' | undefined }) {
  useAdoptServerTheme(pref);
  return null;
}

/** Route guard: requires a signed-in user with a selected context of the given kind. */
function RequireContext({ kind, children }: { kind: 'staff' | 'client'; children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.isLoading) return <Boot />;
  if (s.isError && !s.user) {
    return (
      <div className="boot">
        <ErrorState error={s.error} onRetry={() => void s.refresh()} title="We couldn’t load your session" />
      </div>
    );
  }
  if (!s.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (location.pathname === '/account') {
    if (isPlatformOperator(s.user)) return <Navigate to="/platform/account" replace />;
    if (s.principal?.kind === 'client') return <Navigate to="/portal/account" replace />;
  }
  // No selected workspace: platform operators go to platform administration; everyone else to the
  // existing workspace picker / no-workspace screens (homePathFor keeps that behaviour unchanged).
  if (!s.principal) return <Navigate to={homePathFor(s)} replace />;
  if (s.principal.kind !== kind) return <Navigate to={homePathFor(s)} replace />;
  return <>{children}</>;
}

/**
 * Route guard for platform administration. Only hides the UI from other users: every /platform API
 * independently requires platformRole on the server. Works on direct navigation and page refresh.
 */
function RequirePlatformOperator({ children }: { children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.isLoading) return <Boot />;
  if (s.isError && !s.user) {
    return (
      <div className="boot">
        <ErrorState error={s.error} onRetry={() => void s.refresh()} title="We couldn’t load your session" />
      </div>
    );
  }
  if (!s.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!isPlatformOperator(s.user)) return <Navigate to={homePathFor(s)} replace />;
  return <>{children}</>;
}

/** Sends signed-in users away from sign-in/sign-up screens. */
function PublicOnly({ children }: { children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.isLoading) return <Boot />;
  // The sign-up page keeps its payment modal open after the account is created (payment pending).
  if (s.user && !(s.signupPayment && location.pathname === '/signup')) {
    const next = new URLSearchParams(location.search).get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    const nextAllowed = !!safeNext && (!!s.principal || (isPlatformOperator(s.user) && safeNext.startsWith('/platform')));
    return <Navigate to={nextAllowed ? safeNext! : homePathFor(s)} replace />;
  }
  return <>{children}</>;
}

/** Requires a signed-in user; no workspace needed (e.g. mandatory 2FA enrolment). */
function RequireSignedIn({ children }: { children: ReactNode }) {
  const s = useSession();
  const location = useLocation();
  if (s.isLoading) return <Boot />;
  if (!s.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

function HomeRedirect() {
  const s = useSession();
  if (s.isLoading) return <Boot />;
  return s.user ? <Navigate to={homePathFor(s)} replace /> : <LandingPage />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

export function AppRoutes() {
  const session = useSession();
  const location = useLocation();
  // Checkout-only sessions never mount application shells or their data requests.
  if (!session.isLoading && session.signupPayment && location.pathname !== '/signup') {
    if (session.mfa?.enrollmentRequired) {
      return location.pathname === '/security/two-factor' ? <TwoFactorSettingsPage /> : <Navigate to="/security/two-factor" replace />;
    }
    if (location.pathname === '/signup/payment') return <SignupPaymentPage />;
    if (session.signupPayment.intentId && location.pathname === `/checkout/${session.signupPayment.intentId}`) {
      return <Routes><Route path="/checkout/:intentId" element={<CheckoutPage />} /></Routes>;
    }
    return <Navigate to="/signup/payment" replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><SignupPage /></PublicOnly>} />
      <Route path="/signup/payment" element={<RequireSignedIn><SignupPaymentPage /></RequireSignedIn>} />
      <Route path="/login/2fa" element={<TwoFactorChallengePage />} />
      {/* Test-gateway checkout: a full-page flow for the signed-in organisation (the API scopes the intent to the tenant). */}
      <Route path="/checkout/:intentId" element={<RequireContext kind="staff"><CheckoutPage /></RequireContext>} />
      <Route path="/security/two-factor" element={<RequireSignedIn><TwoFactorSettingsPage /></RequireSignedIn>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/workspaces" element={<MaintenanceGate><WorkspacePickerPage /></MaintenanceGate>} />
      <Route path="/no-workspace" element={<MaintenanceGate><NoWorkspacePage /></MaintenanceGate>} />

      <Route element={<RequirePlatformOperator><PlatformShell /></RequirePlatformOperator>}>
        <Route path="/platform/search" element={<SearchPage />} />
        <Route path="/platform" element={<PlatformDashboardPage />} />
        <Route path="/platform/account" element={<AccountPage />} />
        <Route path="/platform/notifications" element={<NotificationsPage platform />} />
        <Route path="/platform/more/products" element={<OtherProductsPage platform />} />
        <Route path="/platform/more/suggest-feature" element={<SuggestFeaturePage platform />} />
        <Route path="/platform/more/gamification" element={<GamificationPage platform />} />
        <Route path="/platform/health" element={<PlatformHealthPage />} />
        <Route path="/platform/audit" element={<PlatformAuditPage />} />
        <Route path="/platform/audits" element={<Navigate to="/platform/audit" replace />} />
        <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
        <Route path="/platform/tenants/:id" element={<PlatformTenantDetailPage />} />
        <Route path="/platform/users" element={<PlatformUsersPage />} />
        <Route path="/platform/support-access" element={<PlatformSupportGrantsPage />} />
        <Route path="/platform/support-tickets" element={<SupportTickets platform />} />
        <Route path="/platform/support-grants" element={<Navigate to="/platform/support-access" replace />} />
        <Route path="/platform/plans" element={<PlatformPlansPage />} />
        <Route path="/platform/billing" element={<PlatformBillingPage />} />
        <Route path="/platform/countries" element={<PlatformCountriesPage />} />
        <Route path="/platform/coupons" element={<PlatformCouponsPage />} />
        <Route path="/platform/referrals" element={<PlatformReferralsPage />} />
        <Route path="/platform/email" element={<PlatformEmailPage />} />
        <Route path="/platform/email-logs" element={<PlatformEmailLogsPage />} />
        <Route path="/platform/inactivity-reminders" element={<PlatformInactivityPage />} />
        <Route path="/platform/usage-reports" element={<PlatformUsageReportsPage />} />
        <Route path="/platform/announcements" element={<CommunicationsPage />} />
        <Route path="/platform/updates" element={<Navigate to="/platform/announcements?view=updates" replace />} />
        <Route path="/platform/impersonation" element={<PlatformImpersonationPage />} />
        <Route path="/platform/security" element={<PlatformSecurityPage />} />
        <Route path="/platform/google-login" element={<PlatformGoogleLoginPage />} />
        <Route path="/platform/ip-blocker" element={<PlatformIpBlockerPage />} />
        <Route path="/platform/maintenance" element={<PlatformMaintenancePage />} />
        <Route path="/platform/login-alerts" element={<PlatformLoginAlertsPage />} />
        <Route path="/platform/ai/usage" element={<PlatformAiUsagePage />} />
        <Route path="/platform/ai/providers" element={<PlatformAiProvidersPage />} />
        <Route path="/platform/ai/keys" element={<PlatformAiKeysPage />} />
        <Route path="/platform/ai/firewall" element={<PlatformAiFirewallPage />} />
        <Route path="/platform/settings" element={<PlatformSettingsPage />} />
        <Route path="/platform/appearance" element={<PlatformAppearancePage />} />
        <Route path="/platform/connectors" element={<PlatformConnectorsPage />} />
        <Route path="/platform/api" element={<PlatformApiPage />} />
        <Route path="/platform/white-label" element={<PlatformWhiteLabelPage />} />
        <Route path="/platform/*" element={<NotFound backTo="/platform" backLabel="Back to platform administration" />} />
      </Route>

      <Route element={<RequireContext kind="staff"><MaintenanceGate><StaffShell /></MaintenanceGate></RequireContext>}>
        <Route path="/search" element={<SearchPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/more" element={<MoreHome />} />
        <Route path="/more/products" element={<OtherProductsPage />} />
        <Route path="/more/suggest-feature" element={<SuggestFeaturePage />} />
        <Route path="/more/gamification" element={<GamificationPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientWorkspacePage />} />
        <Route path="/engagements" element={<EngagementsPage />} />
        <Route path="/engagements/new" element={<NewEngagementPage />} />
        <Route path="/engagements/:id" element={<EngagementPage />} />
        <Route path="/engagements/:id/review" element={<ReviewWorkspacePage />} />
        <Route path="/engagements/:id/forms/:formId" element={<StaffFormPage />} />
        <Route path="/review" element={<ReviewQueuePage />} />
        <Route path="/library/templates" element={<TemplatesPage />} />
        <Route path="/library/templates/:id" element={<TemplateBuilderPage />} />
        <Route path="/library/packages" element={<PackagesPage />} />
        <Route path="/library/packages/:id" element={<PackageDetailPage />} />
        <Route path="/automation" element={<AutomationPage />} />
        <Route path="/automations" element={<AutomationPage />} />
        <Route path="/audit" element={<AuditExportsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/updates" element={<LatestUpdatesPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="*" element={<NotFound backTo="/dashboard" backLabel="Go to dashboard" />} />
      </Route>

      <Route element={<RequireContext kind="client"><MaintenanceGate><PortalShell /></MaintenanceGate></RequireContext>}>
        <Route path="/portal/search" element={<SearchPage />} />
        <Route path="/portal" element={<PortalOverviewPage />} />
        <Route path="/portal/account" element={<AccountPage />} />
        <Route path="/portal/more" element={<MoreHome />} />
        <Route path="/portal/more/products" element={<OtherProductsPage />} />
        <Route path="/portal/more/suggest-feature" element={<SuggestFeaturePage />} />
        <Route path="/portal/more/gamification" element={<GamificationPage />} />
        <Route path="/portal/forms/:formId" element={<PortalFormPage />} />
        <Route path="/portal/approvals/:engagementId" element={<PortalApprovalPage />} />
        <Route path="/portal/tasks" element={<PortalTasksPage />} />
        <Route path="/portal/assets" element={<PortalAssetsPage />} />
        <Route path="/portal/people" element={<PortalPeoplePage />} />
        <Route path="/portal/profile" element={<PortalProfilePage />} />
        <Route path="/portal/notifications" element={<NotificationsPage />} />
        <Route path="/portal/*" element={<NotFound backTo="/portal" backLabel="Back to overview" />} />
      </Route>

      <Route path="*" element={<div className="boot"><NotFound /></div>} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ThemeBridge>
          <MotionConfig reducedMotion="user">
            <TooltipProvider>
              <ToastProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <ImpersonationBanner />
                  <PasswordChangeGate />
                  <Suspense fallback={<Boot />}>
                    <AppRoutes />
                  </Suspense>
                </BrowserRouter>
              </ToastProvider>
            </TooltipProvider>
          </MotionConfig>
        </ThemeBridge>
      </SessionProvider>
    </QueryClientProvider>
  );
}
